<template>
    <div
        v-if="!isSessionLoading"
        class="study"
        tabindex="-1"
        :key="`${isDarkMode}`"
    >
        <SVGDefinitions />
        <PortalTarget name="modal" multiple />
        <PortalTarget name="sidepanel" multiple />
        <PortalTarget name="quizResultHeader" />
        <UpgradeModal v-if="isShowingUpgradeModal" />
        <RedeemGiftModal
            v-if="isShowingRedeemGiftModal"
        />
        <DeleteRequestedModal v-if="showDeleteRequestedModal" />
        <NameModal
            v-if="showNameModal && orgName"
            :org-name="orgName"
            @close="orgName = null"
        />
        <ExamVersionMessaging v-if="!isShowingUpgradeModal" />
        <AddExamSidePanel
            v-if="!currentExamMetadata
                && currentUser
                && !isLoading
                && route.name !== 'register'
                && route.name !== 'invite'
                && route.name !== 'uti-discount'
            "
            key="missingExamAddExamSidePanel"
            :show-full-height="true"
            @close="closeAddExamSidePanel"
        />
        <AddExamRequiredModal
            v-if="showAddExamRequiredModal"
            @close="showAddExamRequiredModal = false"
        />
        <SSOTransitionModal
            v-if="showSSOTransitionModal && !isLoading"
            :orgName="ssoOrgName"
            @close="showSSOTransitionModal = false"
        />
        <Head v-if="isHeadVisible()" />
        <div
            class="study__body"
            tabindex="-1"
            :style="{
                height: isHeadVisible()
                    ? 'calc(100% - 54px)'
                    : (route.name === 'quiz-result' && breakpoint !== 'black-bear')
                        ? 'calc(100% - 64px)'
                        : '100%'
            }"
        >
            <RouterView />
        </div>
        <Toast
            v-if="toast"
            :is-dark-mode="isDarkMode"
            @close="clearToast"
        >
            {{ toast.title }}
            <template v-if="toast.subtext" #subtext>
                {{ toast.subtext }}
            </template>
        </Toast>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Head from '@/components/Head.vue'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { isHeadVisible } from '@/router'
import { toastModule } from '@/store/toast/module'
import NameModal from '@/components/NameModal.vue'
import { licenseModule } from '@/store/license/module'
import { screenModule } from '@/store/screen/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { mockExamModule } from '@/store/mockExam/module'
import AddExamSidePanel from '@/components/Settings/AddExamSidePanel.vue'
import AddExamRequiredModal from '@/components/AddExamRequiredModal.vue'
import DeleteRequestedModal from '@/components/DeleteRequestedModal.vue'
import { quizModule } from '@/store/quiz/module'
import { getCookie, clearCookie, type PPQueryParams } from '@/utils'
import * as Sentry from '@sentry/browser'
import SSOTransitionModal from '@/components/SSOTransitionModal.vue'
import ExamVersionMessaging from '@/components/Study/ExamVersionMessaging.vue'
import { subscriptionModule } from '@/store/subscription/module'
import UpgradeModal from '@/components/UpgradeModal.vue'
import { upgradeModule } from '@/store/upgrade/module'
import { decrypt } from '@/data-utils'
import { analyticsModule } from '@/store/analytics/module'
import { mobileModule } from '@/store/mobile/module'
import { qotdModule } from '@/store/qotd/module'
import { giftModule } from './store/gift/module'
import RedeemGiftModal from '@/components/Gift/RedeemGiftModal.vue'
import { notebookModule } from './store/notebook/module'

const {
    Toast,
    SVGDefinitions,
} = UIKit

const route = useRoute()
const router = useRouter()

const orgName = ref<string | null>(null)
const showAddExamRequiredModal = ref(false)
const isLoading = ref(true)
const isSessionLoading = ref(true)
const bodyClassObserver = ref<MutationObserver | null>(null)
const showSSOTransitionModal = ref(false)
const ssoOrgName = ref('')
const isOnboardingCompleted = ref<boolean | undefined>(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const toast = computed(() => {
    return toastModule.state.toast
})

const showNameModal = computed(() => {
    return !!orgName.value && !userModule.state.user?.firstName && !userModule.state.user?.lastName
})

const isShowingUpgradeModal = computed(() => {
    return upgradeModule.getters.getIsShowingUpgradeModal()
})

const showDeleteRequestedModal = computed(() => {
    return !!userModule.state.user?.deleteRequestedDate
})

const currentUser = computed(() => {
    return userModule.state.user
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentBundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const currentSubscriptionPlanLength = computed(() => {
    return subscriptionModule.getters.getSubscriptionPlanLengthByExamId(currentExamMetadata.value?.objectId)
})

const currentUserOnboardingCompleted = computed(() => {
    return currentUser.value?.onboardingCompleted
})

const isOOAP = computed(() => {
    return mobileModule.getters.isInArrivedFromMobileState()
})

const crossPlatformConfig = computed(() => {
    return userModule.getters.getCrossPlatformConfig()
})

const activeQuiz = computed(() => {
    return quizModule.getters.getActiveQuiz()
})

const currentQuestion = computed(() => {
    return activeQuiz.value?.questions?.[activeQuiz.value.currentQuestionIndex]
})

const isShowingRedeemGiftModal = computed(() => {
    return giftModule.getters.getIsShowingRedeemGiftModal()
})

onMounted(async () => {
    await router.isReady() // Wait for router to finish initializing so that route.query exists

    const userAlreadySignedIn = !!currentUser.value

    const { signedInWithQuery } = await checkForQuerySession()
    await processMobileStateFromQueryParams({ signedInWithQuery })
    isSessionLoading.value = false

    if (!signedInWithQuery) {
        const sessionCookieName = import.meta.env.VUE_APP_SESSION_COOKIE_NAME
        const sessionCookie = sessionCookieName && getCookie(sessionCookieName)
        if (sessionCookie && !currentUser.value) {
            await userModule.actions.signInWithSession(sessionCookie)

            const hasSeenSSOTransitionModal = userModule.state.user?.webConfig?.hasSeenSSOTransitionModal
            if (!hasSeenSSOTransitionModal && typeof route.query.ssoOrgName === 'string') {
                showSSOTransitionModal.value = true
                ssoOrgName.value = route.query.ssoOrgName
                await userModule.actions.updateWebConfig({
                    hasSeenWelcomeModal: true,
                    hasSeenSSOTransitionModal: true,
                })
            }

            clearCookie(sessionCookieName)
            if (route.name !== 'study' && route.name !== 'assignment') {
                if (typeof route.query.redirect === 'string') {
                    const query = Object.fromEntries(new URLSearchParams(route.query.redirect.split('?')[1]))
                    router.push({ path: route.query.redirect, query: query })
                } else {
                    router.push({ name: 'study', query: route.query })
                }
            }
        }
    }

    await processUpgradeQueryParam()

    // make sure are calling this only if we have a current user
    if (currentUser.value) {
        await Promise.all([
            bundleModule.actions.fetchBundles(),
            examMetadataModule.actions.fetchExamMetadata(),
            userModule.actions.fetchUserData(),
            userExamMetadataModule.actions.fetchUserExamMetadata(),
            notebookModule.actions.fetchNotebookItems().catch(() => { /* noop */ }),
        ])

        if (userAlreadySignedIn) {
            analyticsModule.actions.bootIntercom()
        }
    }

    // Listen for copy event
    window.addEventListener('copy', copyListener)

    // Listen for clearLocalStorage postMessage - for admin iframe support
    window.addEventListener('message', messageListener)

    // due to a difference in how iOS Safari 15+ handles "vh", we have to apply different height styles with newer
    // browsers. Once we have the majority of users on Safari 15+, we can remove this conditional code and apply the
    // vh styles universally. More here: https://lukechannings.com/blog/2021-06-09-does-safari-15-fix-the-vh-bug/
    const iOSVersion = navigator.userAgent.match(/iPhone OS ([0-9]{2})_/)
    const iOSVersionNumber = iOSVersion && Number(iOSVersion[1] || 0)
    const iOS15 = iOSVersion
        && iOSVersionNumber
        && iOSVersionNumber > 14
        && iOSVersionNumber < 17
        && !navigator.userAgent.includes('CriOS')

    if (iOS15) {
        document.body.classList.add('ios-15')
    }

    await fetchUserDataAndUpdateOrgName()
    isDarkModeChanged(isDarkMode.value)

    await checkIfHasCurrentExamMetadata()

    isLoading.value = false

    // listen for the body class to change so we can toggle aria-hidden on study__body
    bodyClassObserver.value = new MutationObserver(toggleAriaHidden)
    bodyClassObserver.value.observe(document.body, { attributes: true })
    toggleAriaHidden()

    if (currentUser.value) {
        await quizModule.actions.fetchAnsweredQuestions()
    }

    await mockExamModule.actions.fetchMockExams()   // Used to need after leanplum, TODO: maybe could move up now

    if (
        route.query.payment_intent
        && route.query.payment_intent_client_secret
        && route.query.redirect_status
    ) {
        const userId = currentUser.value?.objectId
        const redirectStatus = route.query.redirect_status
        Sentry.captureException(new Error(`Stripe: got redirect_status ${redirectStatus} for user ${userId}`))
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('message', messageListener)
    window.removeEventListener('copy', copyListener)
    bodyClassObserver.value?.disconnect()
})

const messageListener = (event: MessageEvent) => {
    if (event.data === 'clearLocalStorage') {
        localStorage.clear()
    }
}

const copyListener = () => {
    // only tracking text selection in specific views
    const routeName = route.name
    const listeningRoutes = [ 'study', 'quiz', 'question-review', 'qotd', 'mock-exam' ]
    if (typeof routeName === 'string' && !listeningRoutes.includes(routeName)) {
        return
    }

    // get selection information
    const selection = window.getSelection()
    const selectedText = selection?.toString().trim()
    const selectedTextWordCount = (selectedText?.match(/\b\w+\b/g) || []).length
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : undefined
    const commonAncestorContainer = range?.commonAncestorContainer
    const elemContainer = commonAncestorContainer?.nodeType === Node.ELEMENT_NODE
        ? commonAncestorContainer
        : commonAncestorContainer?.parentElement

    // if no text was selected or the containing element can't be determined, just return
    if (!selectedText || !(elemContainer instanceof HTMLElement)) {
        return
    }
    const closestDiv = elemContainer.closest('div')

    // determine selection location so we can get class and figure out context
    const containerClass = closestDiv?.className
    if (!containerClass) {
        return
    }

    // name text selection location based on containing element class
    let textSelectionLocation = 'unknown'
    if (containerClass.includes('prompt')) {
        textSelectionLocation = 'prompt'
    } else if (containerClass.includes('choice')) {
        textSelectionLocation = 'choice'
    } else if (containerClass.includes('keyword-definition')) {
        textSelectionLocation = 'keyword-definition'
    } else if (containerClass.includes('explanation')) {
        textSelectionLocation = 'explanation'
    } else if (containerClass.includes('passage')) {
        textSelectionLocation = 'passage'
    } else if (containerClass.includes('tutor')) {
        textSelectionLocation = 'tutor'
    }

    let serial = currentQuestion.value?.serial
    let questionContext = 'quiz'
    if (routeName === 'qotd') {
        const quizId = route.params['quizId'] as string | undefined
        const quiz = quizId && quizModule.getters.getQuizById(quizId)

        if (quiz) {
            const answer = quiz.answers[0]

            serial = answer?.questionSerial
        } else {
            serial = qotdModule.getters.getQotDQuestion()?.serial
        }
    }
    if (routeName === 'study') {
        serial = qotdModule.getters.getQotDQuestion()?.serial
        questionContext = 'qotd'
    } else if (routeName === 'question-review') {
        serial = route.query.questionSerial as string
        questionContext = 'question-review'
    }

    analyticsModule.actions.amplitudeTrack(
        'Question Text Copied',
        {
            serial: serial ?? 'unknown',
            textSelectionLocation,
            selectedText,
            selectedTextWordCount,
            questionContext,
        }
    )
}

const toggleAriaHidden = () => {
    const bodyClasses = Array.from(document.body.classList)
    const bodyCovered = bodyClasses.find(c =>
        c === 'uikit-sidepanel-open'
        || c === 'uikit-modal-open'
        || c === 'mobile-menu-open'
    )
    const headCovered = bodyClasses.find(c =>
        c === 'byoq-modal-open'
        || c === 'uikit-modal-open'
    )

    const studyBody = document.querySelector('.study__body')
    setTimeout(() => {
        if (bodyCovered) {
            studyBody?.setAttribute('aria-hidden', 'true')
        } else {
            studyBody?.removeAttribute('aria-hidden')
        }

        const studyHead = document.querySelector('.head')
        if (headCovered) {
            studyHead?.setAttribute('aria-hidden', 'true')
        } else {
            studyHead?.removeAttribute('aria-hidden')
        }
    }, 500)
}

const extractTokenFromQueryParams = async (queryParams: PPQueryParams) => {
    // Bail out with undefined if no queryParam
    let querySessionToken: string | undefined = undefined
    const userData = queryParams.userData
    if (userData === undefined) return Promise.resolve(undefined)

    // process the queryParam and see if it can be decoded
    const parseAppId = import.meta.env.VUE_APP_PARSE_APP_ID
    const date = new Date()
    const minuteAgoDate = new Date()
    minuteAgoDate.setMinutes(minuteAgoDate.getMinutes() - 1)
    const dateString = date.toISOString().substring(0, 16)
    const minuteAgoDateString = minuteAgoDate.toISOString().substring(0, 16)
    querySessionToken = await decrypt(userData, parseAppId, dateString)
    if (!querySessionToken) {
        querySessionToken = await decrypt(userData, parseAppId, minuteAgoDateString)
    }

    return querySessionToken
}

const checkForQuerySession = async () => {
    let signedInWithQuery = false

    try {
        const queryParams = Object.fromEntries(new URLSearchParams(window.location.search)) as PPQueryParams
        const existingSessionToken = userModule.getters.getSessionToken()
        const querySessionToken = await extractTokenFromQueryParams(queryParams)

        // if we have a sessionToken, adopt it
        if (querySessionToken) {
            // If we're already signed in as the desired user, skip signing out and in again
            // (Trying to sign in using the same signed-out session will fail)
            if (querySessionToken !== existingSessionToken) {
                if (userModule.state.user) {
                    await userModule.actions.signOut()
                }
                await userModule.actions.signInWithSession(querySessionToken)
            }

            // if we were able to load a User after pulling a session token from queryParams
            if (userModule.state.user) {
                signedInWithQuery = true

                // don't show the welcome modal to users signing in with a session token
                await userModule.actions.updateWebConfig({
                    hasSeenWelcomeModal: true,
                    hasSeenOnboarding: true,
                })
            }
        }
    } catch (err) {
        Sentry.captureException(err)
    }

    return {
        signedInWithQuery,
    }
}

const processMobileStateFromQueryParams = async (options?: { signedInWithQuery?: boolean }) => {
    const signedInWithQuery = options?.signedInWithQuery
    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search)) as PPQueryParams

    // If present, adopt the Amplitude Session Id
    if (queryParams.amplitudeSessionId) {
        analyticsModule.actions.setAmplitudeSessionId(queryParams.amplitudeSessionId)
    }

    // Capture values like platform, productIndex, ulPath, priorScreenName, etc
    mobileModule.actions.buildMobileStateFromQueryParams(queryParams)

    if (signedInWithQuery) {
        // capture some extra properties for the event
        // Being overly cautious with platform value to ensure we don't polute Amplitude
        const path = route.path
        const priorScreen = queryParams.priorScreenName
        const platform = mobileModule.getters.getPlatform() || 'Unknown'
        analyticsModule.actions.amplitudeTrack('App To Browser', { path, platform, priorScreen })
    }

    // remove all query parameters related to coming to web from mobile
    await router.replace({
        name: route.name,
        query: {
            ...route.query,
            'userData': undefined,
            'priorScreenName': undefined,
            'amplitudeSessionId': undefined,
            'platform': undefined,
            'productIndex': undefined,
            'ulPath': undefined,
            'bundleId': undefined,
        },
    })
}

const processUpgradeQueryParam = async () => {
    if (route.query.upgrade) {
        upgradeModule.actions.openUpgradeModal({
            upgradePurchasePressed: true,
        })
        await router.replace({
            name: route.name,
            query: {
                ...route.query,
                upgrade: undefined,
            },
        })
    }
}

const closeAddExamSidePanel = () => {
    if (!currentExamMetadata.value) {
        showAddExamRequiredModal.value = true
    }
}

const routeQueryChanged = async () => {
    // Don't re-process query params while still loading.
    // This watcher is to handle cases where these query params
    // are added dynamically AFTER the Study component has mounted
    // (possibly through a redirect, for example)
    if (isLoading.value) {
        return
    }

    const mobileQueryParams = [
        'priorScreenName',
        'amplitudeSessionId',
        'platform',
        'productIndex',
        'ulPath',
    ]
    if (mobileQueryParams.some(param => param in route.query)) {
        await processMobileStateFromQueryParams()
    }

    if (route.query.upgrade) {
        await processUpgradeQueryParam()
    }
}

const fetchUserDataAndUpdateOrgName = async () => {
    await userModule.actions.fetchUserData()
    orgName.value = await licenseModule.actions.fetchUsersLicenseOrgName() || null
}

const checkIfHasCurrentExamMetadata = async () => {
    if (!userModule.state.user) {
        return
    }
    isLoading.value = true

    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        bundleModule.actions.fetchBundles(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])
    isOnboardingCompleted.value = currentUserOnboardingCompleted.value
    isLoading.value = false
}

const checkIsCurrentUserOnboardingCompleted = async () => {
    if (
        !isOnboardingCompleted.value
        && (currentUser.value?.webConfig?.hasSeenWelcomeModal
            || crossPlatformConfig.value?.levelUpFreeTrial?.hasSeenLevelUpTrialSuggestion)
        && currentExamMetadata.value?.examGuid
        && currentBundle.value?.objectId
        && currentSubscriptionPlanLength.value
        && !isOOAP.value
    ) {
        await userModule.actions.onboardingCompleted({
            firstExamGuid: currentExamMetadata.value.examGuid,
            firstBundleId: currentBundle.value.objectId,
            signedUpOn: 'Web',
            onboardingResult: currentSubscriptionPlanLength.value,
        })
    }
}

const clearToast = () => {
    toastModule.actions.clearToast()
}

const isDarkModeChanged = (newVal: boolean) => {
    const body = document.querySelector('body')
    if ((newVal && !body?.className.includes('dark')) || (!newVal && body?.className.includes('dark'))) {
        if (newVal) {
            body?.classList.add('dark')
        } else {
            body?.classList.remove('dark')
        }
    }
}

watch(() => route.query, routeQueryChanged)
watch(currentUser, fetchUserDataAndUpdateOrgName)
watch(currentUser, checkIfHasCurrentExamMetadata)
watch(currentUser, (user, oldUser) => {
    if (user && !oldUser) {
        notebookModule.actions.fetchNotebookItems().catch(() => { /* noop */ })
    }
})
watch(isOnboardingCompleted, checkIsCurrentUserOnboardingCompleted)
watch(isDarkMode, isDarkModeChanged)
</script>

<style lang="scss">
body,
html {
    height: 100%;
    width: 100%;
    overflow: hidden;

    @include breakpoint(black-bear) {
        overflow: scroll;
    }

    // as noted above, once most people are on iOS 15, we can make these styles universal
    &.ios-15 {
        .uikit-page-overlay {
            height: calc(100vh - env(safe-area-inset-bottom, 0px) - 10px) !important;
            max-height: calc(100vh - env(safe-area-inset-bottom, 0px) - 10px) !important;
        }

        .uikit-modal-container,
        .mobile-menu {
            height: calc(100vh - env(safe-area-inset-bottom, 0px)) !important;
            max-height: calc(100vh - env(safe-area-inset-bottom, 0px)) !important;
        }

        .uikit-quiz-container {
            // stylelint-disable-next-line media-feature-name-no-vendor-prefix
            @media screen and (-webkit-min-device-pixel-ratio: 0) {
                /* Safari only override */
                ::i-block-chrome,
                .flex-direction-nav-featured a {
                    height: calc(90vh - env(safe-area-inset-bottom, 0px)) !important;
                    max-height: calc(90vh - env(safe-area-inset-bottom, 0px)) !important;
                }
            }
        }
    }
}

body.dark {
    background: $charcoal;
}

// this disables scrolling on the study__body class when full-screen divs are open which helps prevent double
// scrolling on mobile devices (and probably other devices as well)
body.byoq-modal-open > .study > .study__body,
body.uikit-sidepanel-open > .study > .study__body,
body.uikit-modal-open > .study > .study__body,
body.mobile-menu-open > .study > .study__body {
    overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
.study {
    height: 100%;

    &__body {
        box-sizing: border-box;
        overflow: auto;
        outline: none;

        @include breakpoint(black-bear) {
            overflow: initial;
            height: auto;
        }
    }
}
</style>
